// src/App.js
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { UserProvider } from './context/UserContext';
import { v4 as uuidv4 } from 'uuid';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import api from './utils/api';
import { cachedRequest } from './utils/requestCache';
import { ConsentProvider } from './context/ConsentContext';
import CookieConsentBanner from './components/CookieConsentBanner';
import RequireAuth from "./routeMVP1010/RequireAuth";
import ActivityRedirect from "./components/ActivityRedirect";
import PassiveFeedbackWidget from "./ComponentsMVP1010/common/PassiveFeedbackWidget";
import SmartRoot from "./components/SmartRoot";
import RequireDailyCheckIn from "./routeMVP1010/RequireDailyCheckIn";
import ProtectedWithConsent from "./components/ProtectedWithConsent";
import MoodGate from "./components/MoodGate";
import { handleBackNavigation } from './utils/navigation';

// Critical components (loaded immediately - auth/landing pages)
import Login from './pages/AuthPage.jsx';
import Register from './pages/Register';
import ForgotPasswordPage from './pagesMVP1010/ForgotPasswordPage.jsx';
import ResetPasswordConfirmPage from './pagesMVP1010/ResetPasswordConfirmPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.js';
import TermsOfServicePage from './pages/TermOfServicePage.js';
import ConsentPage from "./pages/ConsentPage";

// Lazy load dashboard and heavy components (code splitting)
const Dashboard = lazy(() => import('./pagesMVP1010/BloomPalAfterMoodcall.jsx'));
const DashboardV2 = lazy(() => import('./pagesMVP1010/BloomPalAfterMoodcallV2.jsx'));
const Home = lazy(() => import('./pages/Home'));
const MoodCallSummary = lazy(() => import('./pagesMVP1010/MoodCallSummary.jsx'));
const BlossomChatPage = lazy(() => import("./ComponentsMVP1010/BlossomChatPage.jsx"));
const BlossomMoodCall = lazy(() => import("./ComponentsMVP1010/MoodCall.jsx"));
const BlossomAgentPage = lazy(() => import("./blossomAgent/BlossomAgentPage.jsx"));
const ActivityPlayerPage = lazy(() => import("./blossomAgent/ActivityPlayerPage.jsx"));
const ViewScheduleGuard = lazy(() => import("./blossomAgent/ViewScheduleGuard.jsx"));
const MoodCall = lazy(() => import('./pagesMVP1010/MoodCallQuickBar.jsx'));
const CalendarSettings = lazy(() => import('./pages/CalendarSettings'));
const ThemePicker = lazy(() => import('./components/ThemePicker.js'));
const Start = lazy(() => import('./pages/ActivityPicker.js'));
const Profile = lazy(() => import('./pagesMVP1010/Profile.tsx'));
const ProfilePage = lazy(() => import('./pagesMVP1010/ProfilePage.tsx'));
const GrowthJourney = lazy(() => import('./pagesMVP1010/GrowthJourney.tsx'));
const AccountSettings = lazy(() => import('./pagesMVP1010/AccountSettings.tsx'));  

// Lazy load activities (heavy components)
const BoxBreathing = lazy(() => import('./ActivitiesMVP1010/BoxBreathing.jsx'));
const SafePlaceVisualization = lazy(() => import('./ActivitiesMVP1010/SafePlaceVisualization.jsx'));
const MiddayMindfulness = lazy(() => import('./ActivitiesMVP1010/MiddayMindfulness.jsx'));
const MindfulPhotography = lazy(() => import('./ActivitiesMVP1010/MindfulPhotography.jsx'));
const WorryParkingLot = lazy(() => import('./ActivitiesMVP1010/WorryParkingLot.jsx'));
const CaffeineCurfewCheckin = lazy(() => import('./ActivitiesMVP1010/CaffeineCurfewCheckin.jsx'));
const CaffeineCurfewIntention = lazy(() => import('./ActivitiesMVP1010/CaffeineCurfewIntention.jsx'));
const EveningWindDown = lazy(() => import('./ActivitiesMVP1010/EveningWindDown.jsx'));
const PowerRestoreNap = lazy(() => import('./ActivitiesMVP1010/PowerRestoreNap.jsx'));
const AlreadyWorthyCheckpoint = lazy(() => import('./ActivitiesMVP1010/AlreadyWorthyCheckpoint/AlreadyWorthyCheckpoint.jsx'));
const PowerPosePage = lazy(() => import("./ActivitiesMVP1010/PowerPose/PowerPosePage.js"));
const ConfidenceMirrorTalkEvidence = lazy(() => import("./ActivitiesMVP1010/ConfidenceMirrorTalkEvidence.jsx"));
const OneThingTrulyMatters = lazy(() => import("./ActivitiesMVP1010/OneThingTrulyMatters.jsx"));
const StandStretchResetActivity = lazy(() => import("./ActivitiesMVP1010/StandStretchResetActivity.jsx"));
const CompassionCheckIn = lazy(() => import("./ActivitiesMVP1010/CompassionCheckIn.jsx"));
const EyeBreakActivity = lazy(() => import("./ActivitiesMVP1010/EyeBreakActivity.jsx"));
const ThoughtReframe = lazy(() => import("./ActivitiesMVP1010/ThoughtReframe"));
const ConfidenceAnchorActivity = lazy(() => import("./ActivitiesMVP1010/ConfidenceAnchorActivity.jsx"));
const FounderResilienceTalk = lazy(() => import("./ActivitiesMVP1010/FounderResilienceTalk.jsx"));

// Lazy load other heavy components
const SoundMixerPage = lazy(() => import('./content/SoundMixerPage.jsx'));
const MixDetailPage = lazy(() => import('./content/MixDetailPage.jsx'));
const TakePhotoActivity = lazy(() => import('./content/TakePhotoActivity.js'));
const MiniRelax = lazy(() => import('./content/MiniRelax.js'));
const EmotionalReset = lazy(() => import('./stfeatures/ereset.jsx'));
const MindfulMoment10s = lazy(() => import('./stfeatures/MindfulMoment10s.jsx'));
const PreReset = lazy(() => import("./stfeatures/prereset.jsx"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: '#FFF4D6'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '18px', color: '#303030', marginBottom: '12px' }}>Loading...</div>
    </div>
  </div>
);

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

// Initialize theme mode from localStorage BEFORE React renders (prevents flash)
(function initializeTheme() {
  const savedMode = localStorage.getItem('appearanceMode');
  if (savedMode === 'calm') {
    document.documentElement.classList.add('calm-mode');
  } else {
    document.documentElement.classList.remove('calm-mode');
  }
})();

const generateSessionIdIfNotExists = () => {
  const existing = localStorage.getItem('sessionId');
  if (!existing) {
    const newSessionId = crypto.randomUUID();
    localStorage.setItem('sessionId', newSessionId);
    console.log('Generated new sessionId:', newSessionId);
  }
};

const ensureCsrfCookie = async () => {
  try {
    // Use cached request to prevent duplicate CSRF calls on mount
    await cachedRequest(api.get, "/api/set-csrf/");
  } catch (err) {
    console.error("CSRF setup failed", err);
  }
};

const markLoginDaily = async () => {
  try {
    // Check if we've already pinged today using localStorage
    const lastPingDate = localStorage.getItem('bp_login_daily_ping_date');
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    if (lastPingDate === today) {
      // Already pinged today, skip
      return;
    }
    
    // Get sessionId for guest users
    const sessionId = localStorage.getItem('sessionId');
    
    // Prepare request body
    const requestBody = sessionId ? { session_id: sessionId } : {};
    
    // Make the request (fire-and-forget, don't block UI)
    api.post('/api/growth/quests/login-daily/', requestBody)
      .then(() => {
        // Mark as pinged for today
        localStorage.setItem('bp_login_daily_ping_date', today);
      })
      .catch((err) => {
        // Silently fail - don't spam console or block UI
        console.debug('Login daily quest ping failed (non-critical):', err);
      });
  } catch (err) {
    // Silently fail
    console.debug('Login daily quest ping error (non-critical):', err);
  }
};

function TakePhotoWrapper() {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<LoadingFallback />}>
      <TakePhotoActivity onComplete={() => navigate('/')} />
    </Suspense>
  );
}
function MiniRelaxWrapper() {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<LoadingFallback />}>
      <MiniRelax onComplete={() => navigate('/')} />
    </Suspense>
  );
}
function MindfulPhotographyWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Suspense fallback={<LoadingFallback />}>
      <MindfulPhotography onComplete={() => {
        // Use handleBackNavigation to respect returnTo context instead of hardcoding '/'
        handleBackNavigation(navigate, location);
      }} />
    </Suspense>
  );
}

const App = () => {
  useEffect(() => {
    let visitorId = localStorage.getItem('visitorId');
    if (!visitorId) {
      visitorId = uuidv4();
      localStorage.setItem('visitorId', visitorId);
    }
    generateSessionIdIfNotExists();
    ensureCsrfCookie();
    // Mark login daily quest (fire-and-forget, debounced via localStorage)
    markLoginDaily();
  }, []);

  const clientId = '974382824692-m5k6et5dcjr7s3mt3u7bk0ikkqqhaub1.apps.googleusercontent.com';

  return (
    <ConsentProvider>
      <GoogleOAuthProvider clientId={clientId}>
        <ThemeProvider>
          <Router>
            {/* UserProvider MUST be inside Router (it uses useNavigate/useLocation)                 <Route path="/" element={<RequireDailyCheckIn>< Dashboard/></RequireDailyCheckIn>} /> //Home1
*/}
            <UserProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/reset-password" element={<ForgotPasswordPage />} />
                  <Route path="/password-reset/confirm/:uid/:token" element={<ResetPasswordConfirmPage />} />
                  <Route path="/Privacy-Policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  {/* Root route - Smart root that shows Dashboard if authenticated, or Landing Page if not */}
                  <Route path="/" element={<SmartRoot />} />
                  {/* Consent gate - users must accept consent before accessing Home */}
                  <Route path="/consent" element={<RequireAuth><ConsentPage /></RequireAuth>} />
                  
                  {/* Dashboard routes - lazy loaded */}
                  <Route path="/home" element={
                    <RequireAuth>
                      <ProtectedWithConsent>
                        <MoodGate><Home /></MoodGate>
                      </ProtectedWithConsent>
                    </RequireAuth>
                  } />
                  <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                  <Route path="/journey" element={<RequireAuth><GrowthJourney /></RequireAuth>} />
                  <Route path="/settings" element={<RequireAuth><AccountSettings /></RequireAuth>} />
                  <Route path="/settings/calendar" element={<RequireAuth><CalendarSettings /></RequireAuth>} />
                  <Route path="/view-schedule" element={<ViewScheduleGuard />} />
                  <Route path="/ChooseYourPal" element={<RequireAuth><ThemePicker /></RequireAuth>} />
                  
                  {/* Redirect all /activities paths to homepage */}
                  <Route path="/activities/*" element={<Navigate to="/" replace />} />
                  
                  <Route path="/mood-summary" element={<RequireAuth><MoodCallSummary /></RequireAuth>} />
                  <Route path="/chat" element={<RequireAuth><BlossomChatPage /></RequireAuth>} />
                  <Route path="/blossom-agent" element={<BlossomAgentPage />} />
                  <Route path="/activity/:id" element={<ActivityPlayerPage />} />
                  <Route path="/BlossomMoodCall" element={<RequireAuth><BlossomMoodCall /></RequireAuth>} />
                  <Route path="/MoodCall" element={<RequireAuth><MoodCall /></RequireAuth>} />
                  
                  {/* Activity routes - lazy loaded */}
                  <Route path="/activities/take-photo" element={<RequireAuth><TakePhotoWrapper /></RequireAuth>} />
                  <Route path="/activities/confidence-anchor" element={<RequireAuth><ConfidenceAnchorActivity /></RequireAuth>} />
                  <Route path="/activities/thought-reframe" element={<RequireAuth><ThoughtReframe /></RequireAuth>} />
                  <Route path="/activities/eye-break" element={<RequireAuth><EyeBreakActivity /></RequireAuth>} />
                  <Route path="/activities/compassion-check-in" element={<RequireAuth><CompassionCheckIn /></RequireAuth>} />
                  <Route path="/activities/micro-break-reset" element={<RequireAuth><StandStretchResetActivity /></RequireAuth>} />
                  <Route path="/activities/one-thing-that-truly-matters" element={<RequireAuth><OneThingTrulyMatters /></RequireAuth>} />
                  <Route path="/activities/ConfidenceMirrorTalk" element={<RequireAuth><ConfidenceMirrorTalkEvidence /></RequireAuth>} />
                  <Route path="/activities/PowerPose" element={<RequireAuth><PowerPosePage /></RequireAuth>} />
                  <Route path="/activities/self-esteem/already-worthy-checkpoint" element={<RequireAuth><AlreadyWorthyCheckpoint /></RequireAuth>} />
                  <Route path="/activities/power-restore-nap" element={<RequireAuth><PowerRestoreNap /></RequireAuth>} />
                  <Route path="/activities/evening-wind-down" element={<RequireAuth><EveningWindDown /></RequireAuth>} />
                  <Route path="/activities/caffeine-cut-off-intention" element={<RequireAuth><CaffeineCurfewIntention /></RequireAuth>} />
                  <Route path="/activities/caffeine-curfew-checkin" element={<RequireAuth><CaffeineCurfewCheckin /></RequireAuth>} />
                  <Route path="/activities/worry-parking-lot" element={<RequireAuth><WorryParkingLot /></RequireAuth>} />
                  <Route path="/activities/mindful-photography" element={<RequireAuth><MindfulPhotographyWrapper /></RequireAuth>} />
                  <Route path="/activities/midday-mindfulness" element={<RequireAuth><MiddayMindfulness /></RequireAuth>} />
                  <Route path="/activities/box-breathing" element={<RequireAuth><BoxBreathing /></RequireAuth>} />
                  <Route path="/activities/safe-place-visualization" element={<RequireAuth><SafePlaceVisualization /></RequireAuth>} />
                  <Route path="/activities/founder-resilience-talk" element={<RequireAuth><FounderResilienceTalk /></RequireAuth>} />
                  
                  {/* Other lazy loaded routes */}
                  <Route path="/Music" element={<RequireAuth><SoundMixerPage /></RequireAuth>} />
                  <Route path="/mixer" element={<RequireAuth><SoundMixerPage /></RequireAuth>} />
                  <Route path="/mixes/:id" element={<RequireAuth><MixDetailPage /></RequireAuth>} />
                  <Route path="/mini-relax" element={<RequireAuth><MiniRelaxWrapper /></RequireAuth>} />
                  <Route path="/reset90" element={<RequireAuth><EmotionalReset /></RequireAuth>} />
                  <Route path="/reset90/intro" element={<RequireAuth><PreReset /></RequireAuth>} />
                  <Route path="/MindfulMoment10s" element={<RequireAuth><MindfulMoment10s /></RequireAuth>} />
                  
                  {/* Redirect old numeric ID routes to slug-based routes */}
                  <Route path="/activities/:id" element={<ActivityRedirect />} />
                  <Route path="*" element={<div>404: Page Not Found</div>} />
                </Routes>
              </Suspense>
              {/* Shows on all pages until consent is given */}
              <CookieConsentBanner />
              {/* Passive Feedback Widget - appears on all pages */}
              <PassiveFeedbackWidget />
            </UserProvider>
          </Router>
          {/*<Footer />*/}
        </ThemeProvider>
      </GoogleOAuthProvider>
    </ConsentProvider>
  );
};

export default App;
